import { Box, Divider, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import TenableConnectForm from "./TenableConnectForm";
import tenable_settings_0 from "./images/tenable_settings_0.png";
import tenable_settings_1 from "./images/tenable_settings_1.png";
import tenable_settings_2 from "./images/tenable_settings_2.png";
import tenable_settings_3 from "./images/tenable_settings_3.png";
import tenable_settings_4 from "./images/tenable_settings_4.png";

const TenableIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("tenable");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerTenableIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerTenableIntegration(values.access_key, values.secret_key)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Tenable integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          "Once integrated, the Breeze platform will automatically read the configurations from Tenable and prioritize the suggested remediations."
        }
        image={logos.TenableLogo}
        name={"tenable"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Typography variant={"h4"}>
              Tenable is already integrated!
            </Typography>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <Typography variant={"h5"}>
                Note: Skip to step 5 if you already have an API keys
              </Typography>
              <Divider sx={{ my: 2 }} />
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Tenable cloud and in the upper menu, click on Settings">
                  {
                    //TODO: research permissions that
                    // Breeze requires https://linear.app/breeze-security/issue/DEV-261/research-tenable-required-permission
                  }
                  <ScreenshotAccordion
                    images={[{ file: tenable_settings_0, altext: "Settings" }]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="In the left-handed menu, click on My Account">
                  <ScreenshotAccordion
                    images={[
                      { file: tenable_settings_1, altext: "Account Settings" },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Then, click on API Keys">
                  <ScreenshotAccordion
                    images={[{ file: tenable_settings_2, altext: "API Keys" }]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Then, click on Generate">
                  <ScreenshotAccordion
                    images={[{ file: tenable_settings_3, altext: "Generate" }]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Copy the access_key and secret_key values here">
                  <ScreenshotAccordion
                    images={[
                      {
                        file: tenable_settings_4,
                        altext: "Copy values",
                        note: "NOTE: the values shown in the screenshot are for illustration purposes only",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Box width={700}>
                    <TenableConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default TenableIntegration;
